import { request } from '@/shared/api/request'
import type { AxiosRequestConfig } from 'axios'
import type { BatchRequestConfig } from '@/shared/api/use-api-batch'

type RequestConfig = AxiosRequestConfig | BatchRequestConfig

// 热词相关接口定义

export interface HotWord {
  id: number
  original_word: string
  translated_word: string
  language_pair: string
  status: 'ai_suggested' | 'confirmed' | 'edited'
  project_id?: number
  dictionary_id?: number  // 热词字典ID，用于更新状态
  is_deleted: boolean
  created_at: string
  updated_at: string
}

export interface HotWordCreate {
  original_word: string
  translated_word: string
  language_pair: string
  project_id?: number
}

export interface HotWordUpdate {
  translated_word?: string
  status?: 'ai_suggested' | 'confirmed' | 'edited'
}

export interface HotWordListResponse {
  items: HotWord[]
  total: number
  page: number
  page_size: number
}

export interface HotWordStats {
  total_count: number
  ai_suggested_count: number
  confirmed_count: number
  edited_count: number
  from_dictionary_count: number
}

// 热词管理API
export const hotWordApi = {
  /**
   * 获取项目热词列表
   */
  getProjectHotWords: (
    projectId: number,
    params?: {
      target_language?: string
      status?: string
      page?: number
      page_size?: number
    },
    config?: RequestConfig,
  ) =>
    request.get<HotWordListResponse>(`/hot-words/projects/${projectId}`, { params, ...config }),

  /**
   * 获取热词统计
   */
  getHotWordStats: (projectId: number, targetLanguage?: string, config?: RequestConfig) =>
    request.get<HotWordStats>(`/hot-words/projects/${projectId}/stats`, {
      params: { target_language: targetLanguage },
      ...config,
    }),

  /**
   * 创建热词
   */
  createHotWord: (data: HotWordCreate) =>
    request.post<HotWord>('/hot-words', data),

  /**
   * 更新热词状态
   */
  updateHotWordStatus: (wordId: number, status: string) =>
    request.put(`/hot-words/dictionary/${wordId}/status`, { status }),

  /**
   * 更新热词
   */
  updateHotWord: (id: number, data: HotWordUpdate) =>
    request.put<HotWord>(`/hot-words/${id}`, data),

  /**
   * 删除热词
   */
  deleteHotWord: (id: number) =>
    request.delete(`/hot-words/${id}`),

  /**
   * 获取热词列表（兼容旧API）
   */
  getHotWords: (params: { project_id: number, project_type: string }) =>
    request.get<HotWord[]>(`/hot-words/projects/${params.project_id}`, {
      params: { project_type: params.project_type }
    }),



  /**
   * 确认热词（兼容旧API）
   */
  confirmHotWord: (id: number) =>
    request.post(`/hot-words/${id}/confirm`),

  /**
   * 提取热词（统一接口，支持视频和文档项目，支持手动添加）
   */
  extractHotWords: (projectId: number, data: { 
    target_language: string, 
    top_n?: number, 
    force_extract?: boolean,
    manual_hot_word?: {
      original_word: string,
      translated_word: string
    }
  }) =>
    request.post<{
      success: boolean
      hot_words: any[]
      total_count: number
      is_from_cache: boolean
      message: string
      project_type: string
      estimated_chars?: number
      quota_info?: any
    }>(`/hot-words/projects/${projectId}/extract-hot-words`, data, {
      timeout: 120000 // 热词提取需要更长时间，设置为120秒
    })
}

// 导出便捷函数
export const getProjectHotWords = hotWordApi.getProjectHotWords
export const getHotWordStats = hotWordApi.getHotWordStats
export const createHotWord = hotWordApi.createHotWord
export const updateHotWord = hotWordApi.updateHotWord
export const updateHotWordStatus = hotWordApi.updateHotWordStatus
export const deleteHotWord = hotWordApi.deleteHotWord
export const getHotWords = hotWordApi.getHotWords
export const confirmHotWord = hotWordApi.confirmHotWord

export default hotWordApi